import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';

const PlanContext = createContext();

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan debe ser usado dentro de PlanProvider');
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const { user, authLoading } = useUser();
  const [plans] = useState({
    trial: {
      name: 'Trial',
      price: 0,
      duration: '7 días',
      color: 'text-gray-400',
      bgColor: 'bg-gray-800',
      features: [
        'Acceso temporal a funciones premium',
        '15 acciones diarias',
        'Meta-IA Orchestrator Multi-Model AI',
        'Experiencia cinematográfica completa',
        'Respuestas de calidad máxima',
        'Historial completo',
        'Cache premium'
      ],
      limitations: [
        'Limitado a 7 días',
        'Sin caja de preguntas premium',
        'Sin exportación avanzada'
      ]
    },
    pro: {
      name: 'Pro',
      price: 5.79,
      duration: 'mes',
      color: 'text-warning',
      bgColor: 'bg-yellow-900/20',
      features: [
        '“Plan Personal IA”: 25 preguntas',
        'Seguimiento semanal personalizado',
        '300 acciones diarias',
        'Herramientas y GPTs básicos',
        'Todas las ventajas del plan Trial',
        'Sin límite de tiempo',
        'Soporte prioritario'
      ],
      limitations: []
    },
    pro_plus: {
      name: 'Pro+',
      price: 9.79,
      duration: 'mes',
      color: 'text-success',
      bgColor: 'bg-green-900/20',
      features: [
        '“Plan Personal IA”: 50 preguntas',
        'Consejos semanales + análisis',
        'Hasta 10,000 acciones diarias',
        'GPTs avanzados (búsqueda, imágenes, voz)',
        'Deep Research completo',
        'Todas las herramientas premium',
        'Exportación avanzada',
        'Acceso a funcionalidades beta'
      ],
      limitations: []
    }
  });

  const [currentPlan, setCurrentPlan] = useState(null);

  // Sincronizar el plan actual con el plan del usuario real (UserContext)
  useEffect(() => {
    try {
      if (authLoading || !user?.id) {
        if (currentPlan !== null) setCurrentPlan(null);
        return;
      }
      const normalized = (user?.plan === 'free') ? 'trial' : (user?.plan || 'trial');
      if (normalized !== currentPlan) setCurrentPlan(normalized);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, user?.plan]);

  // En desarrollo, permitir override del plan vía localStorage para pruebas/e2e
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    try {
      const override = window?.localStorage?.getItem('iv1_plan_override');
      const hasDevSession = !!window?.localStorage?.getItem('iv1_dev_session');
      const valid = ['trial', 'pro', 'pro_plus'];
      if (hasDevSession && override && valid.includes(override) && override !== currentPlan) {
        setCurrentPlan(override);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasFeature = (feature) => {
    if (!currentPlan) return false;
    const plan = plans[currentPlan];
    return plan?.features.some(f => f.toLowerCase().includes(feature.toLowerCase())) ?? false;
  };

  const canUseFeature = (featureName) => {
  const planHierarchy = { trial: 0, free: 0, pro: 1, pro_plus: 2 };
    if (currentPlan == null) return false;
    // Si el featureName es directamente un nombre de plan, interpretarlo como requisito mínimo
    if (planHierarchy.hasOwnProperty(featureName)) {
      return (planHierarchy[currentPlan] ?? 0) >= (planHierarchy[featureName] ?? 0);
    }

    const featureRequirements = {
  'deepweb': 'pro',
      'unlimited-gpts': 'pro_plus',
      'gpt-create': 'pro',
      'project-create': 'pro',
      'project-export': 'pro',
      'propose-export': 'pro',
      'propose-public': 'pro_plus',
      'advanced-tools': 'pro_plus',
      'mass-control': 'free',
      'arconte-ai': 'pro_plus'
    };

    const requiredPlan = featureRequirements[featureName];
    if (!requiredPlan) return true; // si no se define, se considera disponible
    return (planHierarchy[currentPlan] ?? 0) >= (planHierarchy[requiredPlan] ?? 0);
  };

  const upgradePlan = (newPlan) => {
    setCurrentPlan(newPlan);
  };

  const planDebug = {
    rawPlanFromPlansTable: user?.planDebug?.rawPlanFromPlansTable ?? null,
    rawPlanFromProfile: user?.planDebug?.rawPlanFromProfile ?? null,
    normalizedPlan: user?.planDebug?.normalizedPlan ?? currentPlan,
    sourceUsed: user?.planDebug?.sourceUsed ?? 'fallback',
    lastBundleFetchAt: user?.planDebug?.lastBundleFetchAt ?? null,
    bundleErrorCode: user?.planDebug?.bundleErrorCode ?? null,
    bundleErrorMessage: user?.planDebug?.bundleErrorMessage ?? null,
    loadingState: authLoading ? 'loading' : (user?.id ? 'loaded' : 'no-session'),
  };

  // ── Billing world-class fields ─────────────────────────────────────
  const subscriptionStatus = user?.subscriptionStatus || null;
  const currentPeriodEnd = user?.currentPeriodEnd || null;
  const cancelAtPeriodEnd = user?.cancelAtPeriodEnd || false;
  const downgradeReason = user?.downgradeReason || null;
  const hasSubscription = user?.hasSubscription || false;
  // ── Scheduled Upgrade ──────────────────────────────────────────────
  const scheduledPlan = user?.scheduledPlan || null;
  const fsSubscriptionEnd = user?.fsSubscriptionEnd || null;

  const value = {
    plans,
    currentPlan,
    isPlanLoading: authLoading || currentPlan == null,
    hasFeature,
    canUseFeature,
    upgradePlan,
    planDebug,
    // Billing
    subscriptionStatus,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    downgradeReason,
    hasSubscription,
    // Scheduled Upgrade
    scheduledPlan,
    fsSubscriptionEnd,
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};